import { CLUSTER_HOST_SCHEDULING } from 'app/constants'
import { HostNetworkConfiguration } from 'app/plugins/openstack/components/infrastructure/blueprint/model'
import getDataSelector, { getByIdSelector } from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import { virtualMachinesByHostSelector } from 'openstack/components/vms/selectors'
import { intersection, keys, uniq, uniqBy } from 'ramda'
import { storagePoolsSelector } from '../storage/pools/selectors'

import { GPU_NOT_CONFIGURED, UNASSIGNED_CLUSTER } from './constants'
export { GPU_NOT_CONFIGURED, UNASSIGNED_CLUSTER } from './constants'

const roleNamesMap = {
  hypervisor: 'Hypervisor',
  'persistent-storage': 'Persistent Storage',
  'image-library': 'Image Library',
  'pf9-support': 'Advanced Remote Support',
  dns: 'DNS',
  'pf9-ostackhost-neutron-vmw': 'Hypervisor',
  'pf9-glance-role-vmw': 'Hypervisor',
  'pf9-discovery-role': 'Discovery',
}

const roleStatusMap = {
  applied: 'Applied',
  'start-apply': 'Applying role',
  'pre-auth': 'Applying role',
  'auth-converging': 'Applying role',
  'auth-converged': 'Applying role',
  'start-edit': 'Applying role',
  'start-deauth': 'Removing role',
  'pre-deauth': 'Removing role',
  'deauth-converging': 'Removing role',
  'deauth-converged': 'Removing role',
  'not-applied': 'Applying role failed',
  'auth-error': 'Applying role failed',
  'deauth-error': 'Removing role failed',
}

export const healthyStatuses = ['Applied']
export const pendingStatuses = ['Applying role', 'Removing role']
export const errorStatuses = ['Applying role failed', 'Removing role failed']
export const unknownStatuses = ['Unknown']

const getRoleNames = (host) => {
  const v1RoleNames = host.v1Roles?.map((role) => roleNamesMap[role]).filter((name) => !!name)
  const v2RoleNames = host.roles?.map((role) => roleNamesMap[role]).filter((name) => !!name)
  return [...v2RoleNames, ...v1RoleNames]
}

const checkVmwareRolesIncomplete = (host) => {
  const installedRoles = intersection(
    ['pf9-ostackhost-neutron-vmw', 'pf9-glance-role-vmw'],
    host.v1Roles,
  )
  return installedRoles.length > 0 && !host.v1Roles.includes('pf9-discovery-role')
}

export const calcResourceUtilization = (host, hypervisor) => {
  const usage = host?.extensions?.resource_usage?.data || null
  if (!usage || typeof usage === 'string') {
    return null
  }
  const { cpu, memory, disk } = usage

  const K = 1000
  const M = 1000 * K
  const G = 1000 * M
  const Ki = 1024
  const Mi = 1024 * Ki
  const Gi = 1024 * Mi

  return {
    compute: {
      current: cpu.used / G,
      max: cpu.total / G,
      units: 'GHz',
      type: 'used',
    },
    memory: {
      current: (memory.total - memory.available) / Gi,
      max: memory.total / Gi,
      units: 'GiB',
      type: 'used',
    },
    ephemeralDisk: {
      current: disk.used / Gi,
      max: disk.total / Gi,
      units: 'GiB',
      type: 'used',
    },
  }
}

export const calcResourceAllocation = (host, hostStoragePools) => {
  if (!host) return {}
  const hostStorageAllocation = hostStoragePools.reduce(
    (accum, storagePool) => {
      return {
        storage: {
          current: accum.storage.current + storagePool?.capabilities?.allocated_capacity_gb,
          max: accum.storage.max + storagePool?.capabilities?.total_capacity_gb,
          units: 'GB',
        },
      }
    },
    {
      storage: {
        current: 0,
        max: 0,
      },
    },
  )

  return {
    computeAllocation: {
      current: host.vcpus_used || 0,
      max: host.vcpus || 0,
      units: 'vCPUs',
      type: 'allocated',
    },
    memoryAllocation: {
      current: host.memory_mb_used || 0,
      max: host.memory_mb || 0,
      units: 'GiB',
      type: 'allocated',
    },
    diskAllocation: {
      current:
        hostStorageAllocation.storage.current < 0 ? 0 : hostStorageAllocation.storage.current || 0, // added this because allocated_capacity_gb is negative in some cases.
      max: hostStorageAllocation.storage.max || 0,
      units: 'GiB',
      type: 'allocated',
    },
  }
}

const multiCalcResourceUtilization = (hosts, hypervisorsById) => {
  const resourceUsage = hosts.map((host) =>
    calcResourceUtilization(host, hypervisorsById[host?.id]),
  )
  const aggregateUsage = resourceUsage.reduce(
    (accum, usage) => {
      // Not sure how an empty usage was passed in before, but have a
      // way to catch that to avoid crash
      if (!usage?.compute || !usage?.memory || !usage?.disk) {
        return accum
      }
      return {
        compute: {
          ...accum.compute,
          current: accum.compute.current + usage.compute.current,
          max: accum.compute.max + usage.compute.max,
        },
        memory: {
          ...accum.memory,
          current: accum.memory.current + usage.memory.current,
          max: accum.memory.max + usage.memory.max,
        },
        disk: {
          ...accum.disk,
          current: accum.disk.current + usage.disk.current,
          max: accum.disk.max + usage.disk.max,
        },
      }
    },
    {
      compute: {
        current: 0,
        max: 0,
        units: 'GHz',
        type: 'used',
      },
      memory: {
        current: 0,
        max: 0,
        units: 'GB',
        type: 'used',
      },
      disk: {
        current: 0,
        max: 0,
        units: 'GB',
        type: 'used',
      },
    },
  )
  return aggregateUsage
}

// Simple host selector for VM creation form
export const imageLibrayHostSelector = createSharedSelector(
  getDataSelector<DataKeys.ResmgrHosts>(DataKeys.ResmgrHosts),
  (hosts) => {
    return hosts.find(
      (host) =>
        host.roles.includes('image-library') || host.v1Roles.includes('pf9-glance-role-vmw'),
    )
  },
)

export const hypervisorsSelector = createSharedSelector(
  getDataSelector<DataKeys.Hypervisors>(DataKeys.Hypervisors),
  (hypervisors) => {
    return hypervisors.map((hypervisor) => {
      // any model changes go here
      return {
        ...hypervisor,
      }
    })
  },
)

// Filter out host aggregates that are not managed by PCD
export const filteredHostAggregatesSelector = createSharedSelector(
  getDataSelector<DataKeys.HostAggregates>(DataKeys.HostAggregates),
  (hostAggregates) => {
    return hostAggregates?.filter((hostAggregate) => !hostAggregate?.metadata?.['pf9-managed'])
  },
)

export const hostAggregateOptionsSelector = createSharedSelector(
  getDataSelector<DataKeys.HostAggregates>(DataKeys.HostAggregates),
  (hostAggregates) => {
    return hostAggregates.map((hostAggregate) => {
      return {
        label: hostAggregate.name,
        value: hostAggregate.name,
      }
    })
  },
)

export const hostAggregatesByHostIdSelector = createSharedSelector(
  getDataSelector<DataKeys.HostAggregates>(DataKeys.HostAggregates),
  (hostAggregates) => {
    return hostAggregates?.reduce((accum, hostAggregate) => {
      hostAggregate?.hosts?.forEach((hostId) => {
        accum[hostId] = [...(accum?.[hostId] || []), hostAggregate?.name]
      })
      return accum
    }, {} as Record<string, string[]>)
  },
)

export const hostAggregateMetadataByHostIdSelector = createSharedSelector(
  getDataSelector<DataKeys.HostAggregates>(DataKeys.HostAggregates),
  (hostAggregates) => {
    return hostAggregates?.reduce((accum, hostAggregate) => {
      hostAggregate?.hosts?.forEach((hostId) => {
        accum[hostId] = [
          ...(accum?.[hostId] || []),
          {
            hostAggregateName: hostAggregate?.name,
            metadata: hostAggregate?.metadata,
          },
        ]
      })
      return accum
    }, {} as Record<string, { hostAggregateName: string; metadata: any }[]>)
  },
)

const modeString = (str) => {
  return {
    passthrough: 'Passthrough',
    vgpu: 'vGPU',
  }[str]
}

const getCompatibleModes = (host) => {
  const modes = []
  if (host?.extensions?.gpu_info?.data?.passthrough) {
    modes.push('passthrough')
  }
  if (host?.extensions?.gpu_info?.data?.vgpu) {
    modes.push('vgpu')
  }
  return modes
}

// Also have to do this across all hosts and have a hosts by model & vgpu profile selector
const getGpuProfilesByModel = (host) => {
  const gpuPcis = host?.extensions?.gpu_info?.data?.gpu_pci || []
  return gpuPcis.reduce((accum, pci) => {
    const vgpuProfiles = pci?.vgpu_profiles
    if (accum[pci['Model']]) {
      return {
        ...accum,
        [pci['Model']]: uniqBy((profile) => profile.name, [
          ...accum[pci['Model']],
          ...vgpuProfiles,
        ]),
      }
    } else {
      return {
        ...accum,
        [pci['Model']]: vgpuProfiles,
      }
    }
  }, {})
}

const getAllGpuProfiles = (host) => {
  const gpuPcis = host?.extensions?.gpu_info?.data?.gpu_pci ?? []
  const profilesMap = new Map()

  for (const pci of gpuPcis) {
    for (const profile of pci?.vgpu_profiles ?? []) {
      const name = profile.name
      const available = profile.Available ?? 0

      if (!profilesMap.has(name)) {
        profilesMap.set(name, {
          ...profile,
          profileName: profile['Profile name'],
          Available: available,
        })
      } else {
        const existing = profilesMap.get(name)
        existing.Available = Math.max(existing.Available ?? 0, available)
      }
    }
  }

  return Array.from(profilesMap.values())
}

export const resmgrHostsById = getByIdSelector(DataKeys.ResmgrHosts)

export const rawResmgrHostSelector = createSharedSelector(
  getDataSelector<DataKeys.ResmgrHosts>(DataKeys.ResmgrHosts),
  (hosts) => hosts,
)

export const resmgrHostsSelector = createSharedSelector(
  rawResmgrHostSelector,
  virtualMachinesByHostSelector,
  hypervisorsSelector,
  hostAggregatesByHostIdSelector,
  getDataSelector<DataKeys.ClusterHostGroups>(DataKeys.ClusterHostGroups),
  getDataSelector<DataKeys.HostNetworkConfigs>(DataKeys.HostNetworkConfigs),
  hostAggregateMetadataByHostIdSelector,
  storagePoolsSelector,
  (
    hosts,
    vmsByHost,
    hypervisors,
    hostAggregatesByHostIds,
    clusterHostGroups,
    hostConfigs,
    hostAggregatesMetadataByHostIds,
    storagePools,
  ) => {
    const hypervisorsByResmgrId = hypervisors.reduce((accum, hypervisor) => {
      return {
        ...accum,
        [hypervisor?.service?.host]: hypervisor,
      }
    }, {})
    return hosts.map((host) => {
      // any model changes go here
      const ifaces = Object.entries(host?.extensions?.interfaces?.data?.iface_info || {})
        .map(([iface, info]: any) =>
          info?.ifaces?.map((_iface) => ({
            name: iface,
            ip: _iface.addr,
          })),
        )
        .flat()

      const cluster = clusterHostGroups.find((clusterHostGroup) =>
        clusterHostGroup.hostlist?.includes(host.id),
      )
      const matchingHostConfig: HostNetworkConfiguration =
        hostConfigs?.find((config) => config?.id === host?.hostconfig_id) ||
        ({} as HostNetworkConfiguration)

      const physicalNetworkLabels = Object.keys(matchingHostConfig?.networkLabels || {})

      const respondingText =
        host?.info?.responding === undefined
          ? 'online'
          : host?.info?.responding
          ? 'online'
          : 'offline'

      const applicableV1Roles = host?.v1Roles?.filter((role) => {
        return [
          'pf9-support',
          'pf9-ostackhost-neutron-vmw',
          'pf9-glance-role-vmw',
          'pf9-discovery-role',
        ].includes(role)
      })
      const v1RoleStatuses = applicableV1Roles?.map((role) => {
        const status = host?.v1RoleDetails?.[role]
        return {
          role,
          status: respondingText === 'offline' ? 'Unknown' : roleStatusMap[status] || status,
          display: roleNamesMap?.[role] || role,
        }
      })
      const roleStatuses = host?.roles?.map((role) => {
        const status = host?.roles_status_details?.[role]
        return {
          role,
          status: respondingText === 'offline' ? 'Unknown' : roleStatusMap[status] || status,
          display: roleNamesMap?.[role] || role,
        }
      })
      const allRoleStatuses = [...(roleStatuses || []), ...(v1RoleStatuses || [])]

      const hostStoragePools = storagePools?.filter((pool) => pool?.name?.includes(host?.id))
      const allocation = calcResourceAllocation(hypervisorsByResmgrId[host?.id], hostStoragePools)
      const utilization = calcResourceUtilization(host, hypervisorsByResmgrId[host?.id])
      return {
        ...host,
        name: host?.info?.hostname,
        interfaces: ifaces,
        scheduling: hypervisorsByResmgrId[host.id] ?? 'N/A',
        schedulingStatus: CLUSTER_HOST_SCHEDULING[hypervisorsByResmgrId[host?.id]?.status] ?? 'N/A',
        interfacesSearch: ifaces.map((iface) => iface.ip),
        readableInterfaces: ifaces.map((iface) => `${iface.ip} (${iface.name})`),
        displayRoles: getRoleNames(host),
        vmwareRolesIncomplete: checkVmwareRolesIncomplete(host),
        usage: {
          ...utilization,
          ...allocation,
        },
        vms: vmsByHost?.[host.id] || [],
        cluster,
        clusterName: cluster?.name || UNASSIGNED_CLUSTER,
        hostAggregates: hostAggregatesByHostIds?.[host.id] || [],
        hostAggregateMetadata: hostAggregatesMetadataByHostIds?.[host.id] || [],
        physicalNetworkLabels,
        gpuModels: [
          ...new Set(host?.extensions?.gpu_info?.data?.gpu_pci?.map((item) => item['Model'])),
        ],
        deviceIds: host?.extensions?.gpu_info?.data?.gpu_pci?.map((item) => item.pcd_bus_id),
        compatibleModes: getCompatibleModes(host),
        gpuMode: cluster?.gpu?.enabled ? modeString(cluster?.gpu?.mode) : GPU_NOT_CONFIGURED,
        allGpuProfiles: getAllGpuProfiles(host),
        gpuProfilesByModel: getGpuProfilesByModel(host),
        responding: respondingText,
        lastResponseTime: host?.info?.last_response_time,
        roleStatuses: allRoleStatuses,
      }
    })
  },
)

export const resmgrHostsByIdSelector = createSharedSelector(resmgrHostsSelector, (hosts) => {
  return hosts.reduce((accum, host) => {
    return {
      ...accum,
      [host.id]: host,
    }
  }, {})
})

export const allHostGpuModels = createSharedSelector(resmgrHostsSelector, (hosts) => {
  return hosts.reduce((accum, host) => {
    const gpuPcis = host?.extensions?.gpu_info?.data?.gpu_pci || []
    const pciModels = gpuPcis.map((pci) => pci['Model'])
    return uniq([...accum, ...pciModels])
  }, [])
})

export const allGpuProfilesByModel = createSharedSelector(resmgrHostsSelector, (hosts) => {
  return hosts.reduce((accum, host) => {
    // Show vGPU profile from hypervisor hosts only
    if (!host?.roles?.includes('hypervisor')) {
      return accum
    }

    const gpuPcis = host?.extensions?.gpu_info?.data?.gpu_pci || []
    const gpuProfilesByModel = gpuPcis.reduce((accum, pci) => {
      const vgpuProfiles = pci?.vgpu_profiles
      if (accum[pci['Model']]) {
        return {
          ...accum,
          [pci['Model']]: uniqBy((profile) => profile.name, [
            ...accum[pci['Model']],
            ...vgpuProfiles,
          ]),
        }
      } else {
        return {
          ...accum,
          [pci['Model']]: vgpuProfiles,
        }
      }
    }, {})
    const accumModels = keys(accum)
    const combined = accumModels.reduce((combinedAccum, model) => {
      if (accum[model] && gpuProfilesByModel[model]) {
        return {
          ...combinedAccum,
          [model]: uniqBy((profile) => profile.name, [
            ...accum[model],
            ...gpuProfilesByModel[model],
          ]),
        }
      } else {
        return {
          ...combinedAccum,
          [model]: accum[model],
        }
      }
    }, {})
    return {
      ...gpuProfilesByModel,
      ...combined,
    }
  }, {})
})

export const resmgrHostsByGpuModelAndProfile = createSharedSelector(
  resmgrHostsSelector,
  (hosts) => {
    const result = {}
    hosts.forEach((host) => {
      if (host?.extensions?.gpu_info?.data?.gpu_pci?.length > 0) {
        host?.extensions?.gpu_info?.data?.gpu_pci?.forEach((pci) => {
          if (pci?.vgpu_profiles?.length > 0) {
            pci?.vgpu_profiles?.forEach((profile) => {
              if (result?.[pci?.Model]) {
                if (result?.[pci?.Model][profile?.name]) {
                  result[pci?.Model] = {
                    ...result[pci?.Model],
                    [profile?.name]: uniq([...result[pci?.Model][profile?.name], host.id]),
                  }
                } else {
                  result[pci?.Model] = {
                    ...result[pci?.Model],
                    [profile?.name]: uniq([host.id]),
                  }
                }
              } else {
                result[pci?.Model] = {
                  [profile?.name]: uniq([host.id]),
                }
              }
            })
          }
        })
      }
    })
    return result
  },
)

export const hypervisorsByHostIdSelector = createSharedSelector(
  hypervisorsSelector,
  (hypervisors) => {
    return hypervisors.reduce((accum, host) => {
      return {
        ...accum,
        [host?.service?.host]: host,
      }
    }, {})
  },
)

export const hostAggregatesSelector = createSharedSelector(
  getDataSelector<DataKeys.HostAggregates>(DataKeys.HostAggregates),
  resmgrHostsByIdSelector,
  hypervisorsByHostIdSelector,
  (aggregates, resmgrHostsById, hypervisorsById) => {
    return aggregates.map((aggregate) => {
      // any model changes go here
      const resmgrHosts = aggregate.hosts.map((hostId) => resmgrHostsById[hostId])
      const physicalNetworkLabels = resmgrHosts?.[0]?.physicalNetworkLabels
      return {
        ...aggregate,
        clusterName: aggregate.availability_zone || UNASSIGNED_CLUSTER,
        hostInfo: aggregate.hosts.map((hostId) => {
          return {
            name: resmgrHostsById[hostId]?.name,
            ip: hypervisorsById[hostId]?.host_ip,
          }
        }),
        usage: multiCalcResourceUtilization(resmgrHosts, hypervisorsById),
        physicalNetworkLabels,
      }
    })
  },
)

export const hostNetworkConfigsSelector = createSharedSelector(
  getDataSelector<DataKeys.HostNetworkConfigs>(DataKeys.HostNetworkConfigs),
  (configs) => {
    return configs.map((config) => {
      // any model changes go here
      return config
    })
  },
)
